import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  X,
  ArrowUp,
  ArrowDown,
  Package,
  FolderTree,
  Layers,
  Zap,
  Search,
  Save,
  GripVertical,
  Star,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LocationBadge } from '@/components/shared/location-badge';
import { usePosFavorites, useUpdatePosFavorites, type CentralFavorite } from '@/hooks/use-favorites';
import { usePosDevices } from '@/hooks/use-pos-config';
import { useProducts } from '@/hooks/use-products';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { useDepartments } from '@/hooks/use-departments';
import { useCategories } from '@/hooks/use-categories';
import { useLocationStore } from '@/stores/location-store';
import { toast } from '@/stores/toast-store';
import { usePermissions } from '@/hooks/use-permissions';

// ── Action tiles (must match POS FavoriteActionKey names in
//    pos/lib/features/home/data/favorite_actions.dart) ──
const ACTIONS: { key: string; label: string }[] = [
  { key: 'addProduct', label: 'Add Product' },
  { key: 'priceCheck', label: 'Price Check' },
  { key: 'openCashDrawer', label: 'Open Drawer' },
  { key: 'clearCart', label: 'Clear Cart' },
  { key: 'addDiscount', label: 'Add Discount' },
  // Gated on the POS side by the minus_amount flag (POS Config → Store Config):
  // the tile can be pushed to a register from here, but tapping it does nothing
  // until that toggle is on for the location.
  { key: 'minusAmount', label: 'Minus Amount' },
  { key: 'discount5', label: '5% Off' },
  { key: 'discount10', label: '10% Off' },
  { key: 'discount15', label: '15% Off' },
  { key: 'discount20', label: '20% Off' },
  { key: 'reprintLastReceipt', label: 'Reprint Receipt' },
  { key: 'holdOrder', label: 'Hold Order' },
  { key: 'refund', label: 'Refund' },
  { key: 'recallOrder', label: 'Recall Order' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'checkEbtBalance', label: 'EBT Balance' },
  { key: 'sellGiftCard', label: 'Sell Gift Card' },
  { key: 'checkGiftCardBalance', label: 'Gift Card Balance' },
];

type Draft = CentralFavorite & { _key: string };

const typeMeta: Record<string, { icon: React.ElementType; tint: string }> = {
  product: { icon: Package, tint: 'text-blue-600 bg-blue-50' },
  department: { icon: FolderTree, tint: 'text-emerald-600 bg-emerald-50' },
  category: { icon: Layers, tint: 'text-violet-600 bg-violet-50' },
  action: { icon: Zap, tint: 'text-amber-600 bg-amber-50' },
};

function makeKey(f: CentralFavorite) {
  return `${f.type}:${f.reference_id}`;
}

export function FavoritesPage() {
  const { selectedLocationId } = useLocationStore();

  // Scope: 'tenant' = all locations; 'location' = the selected one; 'register'
  // = one specific register at the selected location.
  const [scope, setScope] = useState<'tenant' | 'location' | 'register'>('tenant');
  const [selectedRegisterId, setSelectedRegisterId] = useState('');
  const scopeLocationId = scope === 'tenant' ? '' : selectedLocationId || '';
  const scopeRegisterId = scope === 'register' ? selectedRegisterId : '';

  // Registers at the selected location, for the register-scope picker.
  const { data: devicesData } = usePosDevices(scope === 'register' ? selectedLocationId || undefined : undefined);
  const registers = (devicesData?.data ?? []).filter((d) => d.registerId);

  const { data, isLoading } = usePosFavorites(scopeLocationId, scopeRegisterId);

  const initial: Draft[] = useMemo(
    () =>
      (data?.data ?? [])
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((f) => ({ ...f, _key: makeKey(f) })),
    [data],
  );

  return (
    <div className="space-y-6">
      {/* Scope selector */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-border p-1">
          <button
            onClick={() => setScope('tenant')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              scope === 'tenant' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Tenant-wide (all locations)
          </button>
          <button
            onClick={() => setScope('location')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              scope === 'location'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            This location only
          </button>
          <button
            onClick={() => setScope('register')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              scope === 'register'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Specific register
          </button>
        </div>
        {scope !== 'tenant' && <LocationBadge />}
        {scope === 'register' && selectedLocationId && (
          <select
            value={selectedRegisterId}
            onChange={(e) => setSelectedRegisterId(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">Select a register…</option>
            {registers.map((d) => (
              <option key={d.registerId} value={d.registerId ?? ''}>
                {d.deviceName || `Register ${d.registerId?.slice(0, 8)}`}
              </option>
            ))}
          </select>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {scope === 'tenant'
          ? 'These favorites appear on every register at every location. Registers may also have their own local tiles.'
          : scope === 'location'
            ? selectedLocationId
              ? 'These favorites appear on registers at the selected location, in addition to the tenant-wide set.'
              : 'Select a location from the top bar to edit its favorites.'
            : selectedLocationId
              ? 'These favorites appear only on the chosen register (in addition to the location + tenant-wide sets), and display in the order below.'
              : 'Select a location from the top bar, then choose a register.'}
      </p>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : scope !== 'tenant' && !selectedLocationId ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Select a location from the top bar to edit its favorites.
        </p>
      ) : scope === 'register' && !selectedRegisterId ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Choose a register above to edit its favorites.
        </p>
      ) : (
        // Remount on scope change (and when a fresh response replaces `initial`)
        // so the editor re-seeds its draft from server state without a
        // setState-in-effect. The key folds in the row count + first ref so a
        // reload with different data also resets the editor.
        <FavoritesEditor
          key={`${scopeLocationId}|${scopeRegisterId}|${initial.length}|${initial[0]?._key ?? ''}`}
          initial={initial}
          saveLocationId={scope === 'tenant' ? null : selectedLocationId}
          saveRegisterId={scope === 'register' ? selectedRegisterId || null : null}
          productSearchLocationId={selectedLocationId}
        />
      )}
    </div>
  );
}

// ── The editable surface: owns the draft, seeded once from `initial`. ──
function FavoritesEditor({
  initial,
  saveLocationId,
  saveRegisterId,
  productSearchLocationId,
}: {
  initial: Draft[];
  saveLocationId: string | null;
  saveRegisterId: string | null;
  productSearchLocationId: string | null;
}) {
  const updateMutation = useUpdatePosFavorites();
  // Without Change layout the list is shown as it is, with nothing to change it.
  const canEdit = usePermissions().has('favorites.edit');
  const [draft, setDraft] = useState<Draft[]>(initial);
  const [dirty, setDirty] = useState(false);

  const existingKeys = useMemo(() => new Set(draft.map((d) => d._key)), [draft]);

  function add(fav: CentralFavorite) {
    const key = makeKey(fav);
    if (existingKeys.has(key)) {
      toast.info('Already added');
      return;
    }
    setDraft((d) => [...d, { ...fav, _key: key, sort_order: d.length }]);
    setDirty(true);
  }

  function remove(key: string) {
    setDraft((d) => d.filter((f) => f._key !== key).map((f, i) => ({ ...f, sort_order: i })));
    setDirty(true);
  }

  function move(index: number, dir: -1 | 1) {
    setDraft((d) => {
      const next = d.slice();
      const j = index + dir;
      if (j < 0 || j >= next.length) return d;
      [next[index], next[j]] = [next[j], next[index]];
      return next.map((f, i) => ({ ...f, sort_order: i }));
    });
    setDirty(true);
  }

  // Drag-and-drop reordering: the item numbers (1,2,3…) reflect the order that
  // POS displays; dragging a row to another position renumbers the list.
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  function reorder(from: number, to: number) {
    setDraft((d) => {
      if (from === to || from < 0 || to < 0 || from >= d.length || to >= d.length) return d;
      const next = d.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((f, i) => ({ ...f, sort_order: i }));
    });
    setDirty(true);
  }

  async function save() {
    try {
      await updateMutation.mutateAsync({
        locationId: saveLocationId,
        registerId: saveRegisterId,
        favorites: draft.map((f, i) => ({
          type: f.type,
          reference_id: f.reference_id,
          label: f.label ?? null,
          icon_name: f.icon_name ?? null,
          color_hex: f.color_hex ?? null,
          sort_order: i,
        })),
      });
      toast.success('Favorites saved — POS devices update on next sync');
      setDirty(false);
    } catch {
      toast.error('Failed to save favorites');
    }
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={!dirty || updateMutation.isPending} className="gap-2">
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}

      <div className={canEdit ? 'grid gap-6 lg:grid-cols-[1fr_1.2fr]' : ''}>
        {/* ── Add panel ── */}
        {canEdit && (
          <Card>
            <CardContent className="space-y-6 p-4">
              <ProductAdder locationId={productSearchLocationId} onAdd={add} existingKeys={existingKeys} />
              <DepartmentAdder onAdd={add} existingKeys={existingKeys} />
              <CategoryAdder onAdd={add} existingKeys={existingKeys} />
              <ActionAdder onAdd={add} existingKeys={existingKeys} />
            </CardContent>
          </Card>
        )}

        {/* ── Current favorites (ordered) ── */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold">
              Favorites order <span className="text-muted-foreground">({draft.length})</span>
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">
              {canEdit
                ? 'The number is the position shown on POS — drag a row (or use the arrows) to reorder.'
                : 'The number is the position shown on POS.'}
            </p>
            {draft.length === 0 ? (
              <EmptyState
                icon={Star}
                title="No favorites yet"
                description="Add products, departments, or actions from the left."
                className="py-8"
              />
            ) : (
              <ul className="space-y-2">
                {draft.map((f, i) => {
                  const meta = typeMeta[f.type] ?? typeMeta.action;
                  const Icon = meta.icon;
                  return (
                    <li
                      key={f._key}
                      draggable={canEdit}
                      onDragStart={() => canEdit && setDragIndex(i)}
                      onDragOver={(e) => {
                        if (!canEdit) return;
                        e.preventDefault();
                        if (dragIndex !== null && dragIndex !== i) {
                          reorder(dragIndex, i);
                          setDragIndex(i);
                        }
                      }}
                      onDragEnd={() => setDragIndex(null)}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                        canEdit ? 'cursor-grab active:cursor-grabbing' : ''
                      } ${dragIndex === i ? 'border-primary bg-muted/40' : 'border-border'}`}
                    >
                      {/* Order number (drag to change) */}
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {i + 1}
                      </span>
                      {canEdit && <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />}
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${meta.tint}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{f.label || f.reference_id}</p>
                        <p className="text-xs capitalize text-muted-foreground">{f.type}</p>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={i === 0}
                            onClick={() => move(i, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={i === draft.length - 1}
                            onClick={() => move(i, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-600 hover:bg-red-50"
                            onClick={() => remove(f._key)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Product search-and-add (server-side ilike, same pattern as promotions) ──
function ProductAdder({
  locationId,
  onAdd,
  existingKeys,
}: {
  locationId: string | null;
  onAdd: (f: CentralFavorite) => void;
  existingKeys: Set<string>;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // A scan searches immediately instead of waiting out the debounce.
  const scan = useBarcodeScan((code) => {
    setSearch(code);
    setDebounced(code);
  });
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useProducts(
    { perPage: 25, search: debounced || undefined, locationId: locationId || undefined },
    { enabled: debounced.length > 0 },
  );
  const products: any[] = data?.data ?? [];

  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Package className="h-4 w-4 text-blue-600" /> Products
      </h3>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={scan.onKeyDown}
          onBlur={scan.onBlur}
          placeholder="Search products by name, barcode or SKU…"
          className="pl-9"
        />
      </div>
      {debounced.length > 0 && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-border">
          {isFetching && products.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">Searching…</p>
          ) : products.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No products found.</p>
          ) : (
            products.map((p) => {
              const added = existingKeys.has(`product:${p.id}`);
              return (
                <button
                  key={p.id}
                  disabled={added}
                  onClick={() => onAdd({ type: 'product', reference_id: p.id, label: p.name, sort_order: 0 })}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <span className="truncate">
                    {p.name}
                    {p.sku ? <span className="text-muted-foreground"> ({p.sku})</span> : null}
                  </span>
                  {added ? (
                    <span className="text-xs text-muted-foreground">Added</span>
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Department add ──
function DepartmentAdder({ onAdd, existingKeys }: { onAdd: (f: CentralFavorite) => void; existingKeys: Set<string> }) {
  const { data } = useDepartments();
  const departments: any[] = data?.data ?? [];
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="mb-2 flex w-full items-center gap-2 text-sm font-semibold">
        <FolderTree className="h-4 w-4 text-emerald-600" /> Departments
        <span className="ml-auto text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-border">
          {departments.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No departments.</p>
          ) : (
            departments.map((d) => {
              const added = existingKeys.has(`department:${d.id}`);
              return (
                <button
                  key={d.id}
                  disabled={added}
                  onClick={() => onAdd({ type: 'department', reference_id: d.id, label: d.name, sort_order: 0 })}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <span className="truncate">{d.name}</span>
                  {added ? (
                    <span className="text-xs text-muted-foreground">Added</span>
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Category add ──
function CategoryAdder({ onAdd, existingKeys }: { onAdd: (f: CentralFavorite) => void; existingKeys: Set<string> }) {
  const { data } = useCategories();
  const categories: any[] = data?.data ?? [];
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="mb-2 flex w-full items-center gap-2 text-sm font-semibold">
        <Layers className="h-4 w-4 text-violet-600" /> Categories
        <span className="ml-auto text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-border">
          {categories.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No categories.</p>
          ) : (
            categories.map((c) => {
              const added = existingKeys.has(`category:${c.id}`);
              return (
                <button
                  key={c.id}
                  disabled={added}
                  onClick={() => onAdd({ type: 'category', reference_id: c.id, label: c.name, sort_order: 0 })}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <span className="truncate">{c.name}</span>
                  {added ? (
                    <span className="text-xs text-muted-foreground">Added</span>
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Action add ──
function ActionAdder({ onAdd, existingKeys }: { onAdd: (f: CentralFavorite) => void; existingKeys: Set<string> }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="mb-2 flex w-full items-center gap-2 text-sm font-semibold">
        <Zap className="h-4 w-4 text-amber-600" /> Actions
        <span className="ml-auto text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="flex flex-wrap gap-2">
          {ACTIONS.map((a) => {
            const added = existingKeys.has(`action:${a.key}`);
            return (
              <button
                key={a.key}
                disabled={added}
                onClick={() => onAdd({ type: 'action', reference_id: a.key, label: a.label, sort_order: 0 })}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40"
              >
                {added ? null : <Plus className="h-3 w-3 text-primary" />}
                {a.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
